import inboxIcon from '@/assets/dashboard/inbox.svg'
import coinIcon from '@/assets/dashboard/coin.svg'
import tagIcon from '@/assets/dashboard/tag.svg'
import { Card } from '@/components/ui/card'
import { formatCompactNumber, formatMoney } from '@/utils'
import { StatsCard } from '../stats/StatsCard'
import { ProfileWelcomeCard } from '../ProfileWelcomeCard'
import { BuySellSplit } from '../BuySellSplit'
import TotalProjectsGraph from '../TotalProjectsGraph'
import OrderBookGraph from '../OrderBookGraph'
import AverageDiscountChart from '../AverageDiscountChart'
import { GatedNotice } from '@/components/gate'
import { DashboardStatsProps, formatChartData } from './DashboardStatsBase'
import { statValue } from './DashboardStatsValue'

/**
 * Desktop version of Dashboard Stats
 * 3-column grid layout with charts
 */
export const DashboardStatsDesktop = ({
  profile,
  totalProjects,
  totalOrderbookValue,
  averageDiscount,
  buyOrders,
  sellOrders,
  loading = false,
  financialsHidden = false,
  listedOrders,
  totalProjectsMonthlyAvg,
  orderbookMonthlyAvg
}: DashboardStatsProps) => {
  // For a locked session the projects count is unavailable, but the count of
  // live orders is not — it comes from the public listing the dashboard has
  // already fetched. Swap the tile rather than lock it: the label changes with
  // the figure, because "Total Projects" and "Listed Orders" are not the same
  // number and captioning one with the other's name would be a quiet lie.
  const showListedOrders = financialsHidden && totalProjects === null && listedOrders !== undefined

  return (
    <div className="grid grid-cols-[repeat(3,minmax(0,520px))] gap-4 justify-between items-stretch">
      {/* Left Column - Profile Welcome Card */}
      <div className="col-span-1">
        <ProfileWelcomeCard profile={profile} />
      </div>

      {/* Middle Column - Total Projects & Average Discount */}
      <div className="col-span-1 flex flex-col space-y-4">
        <StatsCard
          icon={inboxIcon}
          iconAlt={showListedOrders ? 'Listed Orders' : 'Total Projects'}
          label={showListedOrders ? 'Listed Orders' : 'Total Projects'}
          helpText={
            showListedOrders
              ? 'Live orders currently on the Secondaries order book.'
              : 'Total number of projects currently listed on SecondLane.'
          }
          value={
            showListedOrders
              ? formatCompactNumber(listedOrders)
              : statValue({
                  value: totalProjects,
                  financialsHidden,
                  label: 'Total projects',
                  widthCh: 4,
                  render: value => formatCompactNumber(value),
                })
          }
          loading={loading}
          chart={
            totalProjectsMonthlyAvg && totalProjectsMonthlyAvg.length > 0 ? (
              <TotalProjectsGraph
                data={formatChartData(totalProjectsMonthlyAvg, 3)}
              />
            ) : undefined
          }
        />
        <StatsCard
          icon={tagIcon}
          iconAlt="Average Discount"
          label="Average Discount"
          helpText="Average discount vs the most recent publicly reported funding-round valuation across listed opportunities."
          value={statValue({
            value: averageDiscount,
            financialsHidden,
            label: 'Average discount',
            widthCh: 4,
            render: value => (
              <>
                <span>{value.toFixed(1)} </span>
                <span className="text-[18px] leading-[32px] text-foreground/48">%</span>
              </>
            ),
          })}
          loading={loading}
          // 🔴 No chart under a figure we do not have. `AverageDiscountChart`
          // draws a fixed decorative shape with no data input at all, so
          // rendering it beneath a blurred or dashed value reads as a chart OF
          // that value — a picture of a number nobody fetched.
          chart={averageDiscount !== null ? <AverageDiscountChart /> : undefined}
        />
      </div>

      {/* Right Column - Order Book & Buy/Sell Split */}
      <div className="col-span-1 flex flex-col space-y-4">
        <StatsCard
          icon={coinIcon}
          iconAlt="Order Book"
          label="Order Book"
          helpText="Total notional value of orders currently on the Secondaries order book."
          value={statValue({
            value: totalOrderbookValue,
            financialsHidden,
            label: 'Total order book value',
            widthCh: 6,
            render: value => (
              <>
                <span className="text-foreground/48">$</span>
                <span className="text-foreground"> {formatMoney(value).replace('$', '').trim()}</span>
              </>
            ),
          })}
          loading={loading}
          chart={
            orderbookMonthlyAvg && orderbookMonthlyAvg.length > 0 ? (
              <OrderBookGraph
                data={formatChartData(orderbookMonthlyAvg, 6)}
              />
            ) : undefined
          }
        />
        <Card variant="default" padding="none" className="rounded-none shadow-none bg-white dark:bg-[#1c1c1c] border border-solid border-white dark:border-[#1c1c1c] relative h-[124px] pt-6 pb-6 pl-6 pr-6">
          {/* 🔴 Never render BuySellSplit without real, non-zero counts.
              `getBuySellPercentages` returns a 50/50 split both for undefined
              counts AND for a genuine 0/0, so an empty order book would be
              painted as a perfectly balanced market that nobody measured. */}
          {buyOrders !== null && sellOrders !== null && buyOrders + sellOrders > 0 ? (
            <BuySellSplit
              buyCount={buyOrders}
              sellCount={sellOrders}
              className="h-[76px]"
            />
          ) : (
            <GatedNotice
              title="Buy / sell split"
              description={
                financialsHidden
                  ? 'Order-flow balance opens up with verification.'
                  : buyOrders === 0 && sellOrders === 0
                    ? 'No orders on the book right now.'
                    : 'We could not load the order-flow balance just now.'
              }
              actionable={financialsHidden}
              className="h-[76px]"
            />
          )}
        </Card>
      </div>
    </div>
  )
}
